import { useCallback } from "react";
import { useOnboardingEvents } from "./onboarding-events";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";
import { GetAvailableRewardsReturnType, requestReward } from "./requests";
import { useUserAndCredits } from "../utils/credits";
import { toast } from "sonner";
import { CoinsIcon } from "lucide-react";
import { useNavigate } from "react-router";
import { getAvailableRewards } from "../rewarding-system-v2/requests";
import { usePostHog } from "posthog-js/react";
import { useAuth } from "@clerk/react-router";

export const allRewardsV2 = [
  "highlight-generate",
  "study-flashcard",
  "use-ai-helps",
  "do-quiz",
  "share-deck",
] as const;

export type Reward = (typeof allRewardsV2)[number];

export const REWARD_AMOUNTS_V2: Record<Reward, number> = {
  "study-flashcard": 10,
  "do-quiz": 10,
  "share-deck": 10,
  "use-ai-helps": 10,
  "highlight-generate": 10,
};

export function useRewardManager() {
  const queryClient = useQueryClient();
  const navigateTo = useNavigate();
  const { isSignedIn } = useAuth();

  const { isLoading, isError, isLoggedIn, isUnlimited, remainingCredits } =
    useUserAndCredits();

  const isOnboardingEnabled = true;

  const posthog = usePostHog();

  const {
    data: rewards,
    isSuccess: availableRewardsLoaded,
    isError: availableRewardsError,
  } = useQuery({
    queryKey: ["available-rewards-v2"],
    queryFn: async () => {
      const data = await getAvailableRewards();

      return data;
    },
    select: (data) => data.availableRewards,
    enabled: !isUnlimited && isOnboardingEnabled && isSignedIn,
  });

  const showRewardToast = useCallback(
    (creditsAwarded: number, reward: Reward) => {
      if (creditsAwarded > 0) {
        triggerRewardToastV2(creditsAwarded, reward);
      }
    },
    [rewards]
  );

  const { mutate: requestRewardMutation } = useMutation({
    mutationKey: ["request-reward-v2"],
    mutationFn: async (reward: Reward) => {
      if (isUnlimited || !rewards || !rewards[reward] || !isOnboardingEnabled) {
        return 0;
      }

      const { creditsAwarded } = await requestReward(reward);

      return creditsAwarded;
    },
    onSuccess: async (creditsAwarded, reward) => {
      if (creditsAwarded > 0) {
        posthog.capture(`Rewarding-System-V2-Reward-Awarded-${reward}`, {
          creditsAwarded,
        });
        showRewardToast(creditsAwarded, reward);
      }
    },
    onMutate: async (reward: Reward) => {
      await queryClient.cancelQueries({ queryKey: ["available-rewards-v2"] });

      const previousRewards =
        queryClient.getQueryData<GetAvailableRewardsReturnType>([
          "available-rewards-v2",
        ]);

      queryClient.setQueryData(
        ["available-rewards-v2"],
        (old: GetAvailableRewardsReturnType) => {
          return old
            ? produce(old, (draft: GetAvailableRewardsReturnType) => {
                delete draft.availableRewards[reward];
              })
            : undefined;
        }
      );

      return { previousRewards };
    },
    onError: async (_, __, context) => {
      queryClient.setQueryData<GetAvailableRewardsReturnType>(
        ["available-rewards-v2"],
        context?.previousRewards
      );
    },
    onSettled: (creditsAwarded) => {
      // Only invalidate queries if we actually received credits
      if (
        isOnboardingEnabled &&
        !isUnlimited &&
        creditsAwarded &&
        creditsAwarded > 0
      ) {
        queryClient.invalidateQueries({ queryKey: ["user-usage"] });
        queryClient.invalidateQueries({ queryKey: ["available-rewards-v2"] });
      }
    },
  });

  const isReady =
    !isLoading && !isError && isLoggedIn && availableRewardsLoaded;

  useOnboardingEvents({
    onFlashcardStudiedToday() {
      posthog.capture("Onboarding-Checklist-10-cards-studied-today");
      requestRewardMutation("study-flashcard");
    },
  });

  const acceptRewardTask = useCallback(
    (reward: Reward, deckName: string) => {
      if (!isReady) {
        return;
      }

      if (reward === "do-quiz" && rewards[reward]) {
        navigateTo(`/quiz/${encodeURIComponent(deckName)}`);
      } else if (reward === "study-flashcard" || reward === "use-ai-helps") {
        navigateTo(`/memo-ai?deck=${encodeURIComponent(deckName)}`);
      } else if (reward === "share-deck") {
        navigateTo(`/deck/${encodeURIComponent(deckName)}`);
      } else if (reward === "highlight-generate") {
        navigateTo(`/deck/${encodeURIComponent(deckName)}?view=pdf`);
      } else {
        return;
      }

      if (remainingCredits < 5) {
        requestRewardMutation(reward);
      }
    },
    [isReady, rewards, navigateTo, remainingCredits]
  );

  const getNextReward = useCallback(() => {
    if (!isReady) {
      return;
    }
    const rewardKey = allRewardsV2.find((reward: Reward) => {
      const rewardData = rewards[reward];
      return rewardData && rewardData.credits > 0;
    });

    if (rewardKey) {
      return { reward: rewardKey, credits: rewards[rewardKey]?.credits ?? 0 };
    } else {
      return undefined;
    }
  }, [isReady, rewards]);

  if (isLoading || !availableRewardsLoaded) {
    return {
      isLoading: true as const,
      isError: false as const,
      isSuccess: false as const,
      availableRewards: undefined,
      acceptRewardTask: undefined,
      requestReward: undefined,
      getNextReward: undefined,
    };
  }

  if (isError || availableRewardsError || !isLoggedIn) {
    return {
      isLoading: false as const,
      isError: true as const,
      isSuccess: false as const,
      availableRewards: undefined,
      acceptRewardTask: undefined,
      requestReward: undefined,
      getNextReward: undefined,
    };
  }

  return {
    isLoading: false as const,
    isError: false as const,
    isSuccess: true as const,
    availableRewards: rewards,
    acceptRewardTask,
    requestReward: requestRewardMutation,
    getNextReward,
  };
}

const REWARD_TOAST_MESSAGES = {
  "do-quiz": "Congrats! You started a quiz. Keep going!",
  "study-flashcard": "Congrats! You studied 10 flashcards. Keep going!",
  "use-ai-helps": "Congrats! You used AI helps. Keep going!",
  "share-deck": "Congrats! You shared a deck. Keep going!",
  "highlight-generate": "Congrats! You generated a highlight. Keep going!",
};

export function triggerRewardToastV2(rewardCredits: number, reward: Reward) {
  toast.message(
    <div className="flex flex-col gap-1 text-primary-950">
      <CoinsIcon className="h-icon-md w-icon-md" />
      <span className="font-semibold">
        You have earned +{rewardCredits} credits
      </span>
      <span>{REWARD_TOAST_MESSAGES[reward]}</span>
    </div>,
    {
      position: "bottom-left",
      closeButton: true,
    }
  );
}
